import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AuthGate } from '../features/auth/auth-gate.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Label } from '../components/ui/label.js';
import { Button } from '../components/ui/button.js';
import { CopyIcon, CheckIcon, FbGlyph, IgGlyph } from '../components/ui/icons.js';
import { StatusPill } from '../components/ui/status-pill.js';
import { cn } from '../lib/utils.js';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';
import { HotelFilterDropdown } from '../features/hotels/hotel-filter-dropdown.js';
import { getFavoriteHotels } from '../features/dashboard/api.js';
import { listPlans, plansByHotelKey, type MonthlyPlan } from '../features/plans/api.js';
import { listContent, type ContentQuery, type ContentRow } from '../features/content/api.js';

/* ------------------------------------------------------------------ */
/* search params                                                      */
/* ------------------------------------------------------------------ */

export interface ApprovedSearch {
  /** Selected hotel (partner) ids — reuses the shared multi-select dropdown. */
  hotel?: string[];
  /** Single monthly-plan filter; only meaningful when exactly one hotel is set. */
  plan?: string;
}

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

function planLabel(p: MonthlyPlan): string {
  return `${p.year}. ${String(p.month).padStart(2, '0')}`;
}

const PLATFORM_META: Record<'fb' | 'ig', { label: string; Glyph: typeof FbGlyph }> = {
  fb: { label: 'Facebook', Glyph: FbGlyph },
  ig: { label: 'Instagram', Glyph: IgGlyph },
};

/* ------------------------------------------------------------------ */
/* copy button                                                        */
/* ------------------------------------------------------------------ */

/**
 * Copies its `text` to the clipboard and flashes a "Másolva ✓" state for ~1.5s.
 * Falls back silently if the Clipboard API is unavailable (older/insecure
 * contexts) — the button simply does nothing rather than throwing.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  return (
    <Button
      type="button"
      variant={copied ? 'primary' : 'outline'}
      size="sm"
      onClick={() => void copy()}
      aria-label="Szöveg másolása"
    >
      {copied ? (
        <>
          <CheckIcon size={14} /> Másolva
        </>
      ) : (
        <>
          <CopyIcon size={14} /> Másolás
        </>
      )}
    </Button>
  );
}

/* ------------------------------------------------------------------ */
/* card                                                               */
/* ------------------------------------------------------------------ */

function ApprovedCard({ row }: { row: ContentRow }) {
  // Only surface platforms the post actually publishes to AND that have copy.
  const sections = row.contents.filter(
    (c) => (c.platform === 'fb' && row.publishToFb) || (c.platform === 'ig' && row.publishToIg),
  );

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-medium text-[var(--ink-700)]">{row.hotelName}</span>
        <span className="shrink-0 font-mono tabular-nums text-meta text-[var(--fg-2)]">
          {formatDate(row.scheduledFor)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        {row.title ? (
          <p className="truncate text-meta text-[var(--fg-muted)]">{row.title}</p>
        ) : (
          <span />
        )}
        <StatusPill status={row.status} size="sm" className="shrink-0" />
      </div>

      {sections.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--app-divider-2)] px-3 py-4 text-center text-meta italic text-[var(--fg-faint)]">
          Nincs szöveg ehhez a poszthoz.
        </p>
      ) : (
        sections.map((c) => {
          const meta = PLATFORM_META[c.platform];
          const Glyph = meta.Glyph;
          return (
            <div
              key={c.platform}
              className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] p-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-meta font-semibold text-[var(--fg-2)]">
                  <Glyph size={14} /> {meta.label}
                </span>
                <CopyButton text={c.body} />
              </div>
              <p className="whitespace-pre-wrap text-body leading-relaxed text-[var(--ink-700)]">
                {c.body}
              </p>
            </div>
          );
        })
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* page                                                               */
/* ------------------------------------------------------------------ */

function ApprovedInner() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const hotelIds = search.hotel ?? [];
  const planId = search.plan;

  const hotelsQuery = useQuery<Hotel[]>({ queryKey: HOTELS_QUERY_KEY, queryFn: listHotels });
  const favoritesQuery = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });

  // Plan dropdown depends on a SINGLE selected hotel; hidden otherwise.
  const singleHotelId = hotelIds.length === 1 ? hotelIds[0]! : null;
  const plansQuery = useQuery<MonthlyPlan[]>({
    queryKey: plansByHotelKey(singleHotelId ?? ''),
    queryFn: () => listPlans(singleHotelId!),
    enabled: !!singleHotelId,
  });

  const query: ContentQuery = useMemo(
    () => ({
      status: ['content_approved', 'ready_to_publish', 'scheduled'],
      ...(hotelIds.length ? { hotelId: hotelIds } : {}),
      ...(planId && singleHotelId ? { monthlyPlanId: planId } : {}),
      sort: 'oldest',
    }),
    [hotelIds, planId, singleHotelId],
  );

  const listQuery = useQuery<ContentRow[]>({
    queryKey: ['content', query],
    queryFn: () => listContent(query),
  });

  const rows = listQuery.data ?? [];
  const hotels = hotelsQuery.data ?? [];

  function setHotelIds(next: string[]) {
    // Changing the hotel selection invalidates a plan filter that no longer
    // maps to exactly one hotel.
    void navigate({
      to: '/app/approved',
      search: next.length ? { hotel: next } : {},
    });
  }

  function setPlanId(next: string) {
    void navigate({
      to: '/app/approved',
      search: next ? { hotel: hotelIds, plan: next } : { hotel: hotelIds },
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="h2-eyebrow">Áttekintés</div>
        <h1 className="mt-1.5 text-h1 font-sans font-medium leading-none tracking-tight text-[var(--ink-700)]">
          Jóváhagyott posztok{' '}
          <span className="font-mono text-h2 font-normal text-[var(--fg-faint)]">
            {rows.length}
          </span>
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Szűrők</CardTitle>
          <CardDescription>
            A jóváhagyott posztok egy helyen — másold ki a szövegeket az ütemezéshez.
          </CardDescription>
        </CardHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Hotel</Label>
            {hotels.length === 0 ? (
              <p className="text-meta text-[var(--fg-faint)]">
                {hotelsQuery.isLoading ? 'Hotelek betöltése…' : 'Nincs elérhető hotel.'}
              </p>
            ) : (
              <HotelFilterDropdown
                hotels={hotels}
                favoriteIds={favoritesQuery.data ?? []}
                selected={hotelIds}
                onChange={setHotelIds}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="approved-plan">Terv</Label>
            <select
              id="approved-plan"
              value={planId ?? ''}
              onChange={(e) => setPlanId(e.target.value)}
              disabled={!singleHotelId || plansQuery.isLoading}
              className="flex h-10 w-full rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-3 text-body disabled:cursor-not-allowed disabled:opacity-60"
            >
              {!singleHotelId ? (
                <option value="">Válassz egy hotelt a szűréshez</option>
              ) : plansQuery.isLoading ? (
                <option value="">Tervek betöltése…</option>
              ) : (
                <>
                  <option value="">Összes terv</option>
                  {(plansQuery.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {planLabel(p)}
                    </option>
                  ))}
                </>
              )}
            </select>
            {!singleHotelId && (
              <p className="text-meta text-[var(--fg-faint)]">
                A terv szerinti szűréshez pontosan egy hotelt válassz ki.
              </p>
            )}
          </div>
        </div>
      </Card>

      {listQuery.isLoading ? (
        <p className="p-6 text-center text-body text-[var(--fg-muted)]">Betöltés…</p>
      ) : listQuery.error ? (
        <p className="p-6 text-center text-body text-[var(--danger)]">
          {(listQuery.error as Error).message}
        </p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-body text-[var(--fg-muted)]">
          Nincs jóváhagyott poszt a szűrőnek megfelelően.
        </p>
      ) : (
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3')}>
          {rows.map((row) => (
            <ApprovedCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function ApprovedPage() {
  return (
    <AuthGate roles={['admin', 'editor', 'viewer']}>
      <ApprovedInner />
    </AuthGate>
  );
}

export const Route = createFileRoute('/app/approved')({
  validateSearch: (s: Record<string, unknown>): ApprovedSearch => {
    const out: ApprovedSearch = {};
    if (Array.isArray(s.hotel)) {
      const hotels = s.hotel.filter((h): h is string => typeof h === 'string' && h.length > 0);
      if (hotels.length) out.hotel = hotels;
    } else if (typeof s.hotel === 'string' && s.hotel.length > 0) {
      out.hotel = [s.hotel];
    }
    if (typeof s.plan === 'string' && s.plan.length > 0) out.plan = s.plan;
    return out;
  },
  component: ApprovedPage,
});
